import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import apiClient from '@/services/api/client';
import { debug } from '@/utils/debug';
import { Logo } from '@/components/Logo';
import { LoadingSpinner } from '@/components/ui';
import type { OnboardingData } from '@/types/signup';

// Stripe publishable key from environment variables
const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (!STRIPE_KEY) {
  throw new Error('Stripe publishable key is not set. Please define VITE_STRIPE_PUBLISHABLE_KEY in your .env file.');
}
const stripePromise = loadStripe(STRIPE_KEY);

// Validation schema
const billingSchema = z.object({
  cardholder_name: z.string().min(1, 'Cardholder name is required'),
  billing_line1: z.string().min(1, 'Address is required'),
  billing_line2: z.string().optional(),
  billing_city: z.string().min(1, 'City is required'),
  billing_state: z.string().min(1, 'State is required'),
  billing_postal_code: z.string().min(1, 'Postal code is required'),
});

type BillingFormData = z.infer<typeof billingSchema>;

function PaymentForm() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [cardError, setCardError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BillingFormData>({
    resolver: zodResolver(billingSchema),
  });

  // Load onboarding data
  useEffect(() => {
    const onboardingDataStr = sessionStorage.getItem('onboarding_data');
    if (onboardingDataStr) {
      const data = JSON.parse(onboardingDataStr) as OnboardingData;
      setOnboardingData(data);
    } else {
      toast.error('Onboarding session not found');
      navigate('/signup');
    }
  }, [navigate]);

  const onSubmit = async (data: BillingFormData) => {
    if (!stripe || !elements || !onboardingData) {
      return;
    }

    setIsProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: data.cardholder_name,
          address: {
            line1: data.billing_line1,
            line2: data.billing_line2 || undefined,
            city: data.billing_city,
            state: data.billing_state,
            postal_code: data.billing_postal_code,
            country: 'US',
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      debug.log('Payment method created:', paymentMethod.id);

      // Send to backend
      const response = await apiClient.post(
        `/onboarding/payment-setup/${onboardingData.member_uuid}`,
        {
          payment_method_id: paymentMethod.id,
          billing_address: {
            name: data.cardholder_name,
            address_line1: data.billing_line1,
            address_line2: data.billing_line2 || null,
            city: data.billing_city,
            state: data.billing_state,
            postal_code: data.billing_postal_code,
            country: 'US',
          },
        }
      );

      debug.log('Payment setup response:', response.data);

      // Success
      const successMessage = response.data.message || 'Payment method added successfully! Your trial has started.';
      toast.success(successMessage, { duration: 4000 });

      // Mark payment setup as completed
      sessionStorage.setItem('payment_setup_completed', 'true');

      debug.log('Payment setup successful, redirecting to onboarding');

      // Redirect to onboarding after showing success message
      setTimeout(() => {
        navigate('/onboarding');
      }, 2000);
    } catch (error: unknown) {
      debug.error('Payment setup error:', error);
      const apiError = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorMessage = apiError.response?.data?.detail || apiError.message || 'Payment setup failed';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!onboardingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" variant="primary" className="mx-auto" label="Loading" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
          <p className="text-gray-600">Let's get to flourishing.</p>
        </div>

        {/* Main Container */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Setup</h2>
            <p className="text-gray-600">Add your payment method to start your trial</p>
          </div>

          {/* Trial Info Banner */}
          <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg p-4 mb-6 text-center">
            <h3 className="font-semibold mb-1">Your Free Trial</h3>
            <p className="text-sm opacity-90">
              You won't be charged until your trial period ends
            </p>
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Cardholder Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cardholder Name *
              </label>
              <input
                {...register('cardholder_name')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
              />
              {errors.cardholder_name && (
                <p className="mt-1 text-sm text-red-600">{errors.cardholder_name.message}</p>
              )}
            </div>

            {/* Card Element */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Information *
              </label>
              <div className="border border-gray-300 rounded-md p-3">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                  onChange={(e) => setCardError(e.error?.message || '')}
                />
              </div>
              {cardError && <p className="mt-1 text-sm text-red-600">{cardError}</p>}
            </div>

            {/* Billing Address */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Address</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1 *
                  </label>
                  <input
                    {...register('billing_line1')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.billing_line1 && (
                    <p className="mt-1 text-sm text-red-600">{errors.billing_line1.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 2
                  </label>
                  <input
                    {...register('billing_line2')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      {...register('billing_city')}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.billing_city && (
                      <p className="mt-1 text-sm text-red-600">{errors.billing_city.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <input
                      {...register('billing_state')}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.billing_state && (
                      <p className="mt-1 text-sm text-red-600">{errors.billing_state.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code *
                  </label>
                  <input
                    {...register('billing_postal_code')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.billing_postal_code && (
                    <p className="mt-1 text-sm text-red-600">{errors.billing_postal_code.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {isProcessing ? 'Processing...' : 'Add Payment Method'}
            </button>

            {/* Test Cards Notice (Development Only) */}
            {import.meta.env.DEV && (
              <div className="bg-yellow-50 border border-yellow-400 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Test Cards (Development Only)</h4>
                <ul className="text-sm text-yellow-800 space-y-1 ml-5 list-disc">
                  <li>
                    <strong>4242 4242 4242 4242</strong> - Successful payment
                  </li>
                  <li>
                    <strong>4000 0000 0000 0002</strong> - Declined card
                  </li>
                  <li>Use any future date and any 3-digit CVC</li>
                </ul>
              </div>
            )}

            {/* Security Notice */}
            <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-600">
              🔒 Your payment information is secured by Stripe. We never store your card details.
            </div>

            {/* Back Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/onboarding')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                ← Back to Onboarding
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function PaymentSetup() {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm />
    </Elements>
  );
}
